import React, { useId, useState } from 'react';
//@ts-ignore
import * as styles from './faq.module.css';
//@ts-ignore
import { Faq as FaqType } from '../Global/DataUtils/faqQueries';
import RichText from '../Global/RichText';
import { BuildLinkHref } from '../Global/BuildLinkHref';
import { getMultiChoice } from '../Global/DataUtils';
import CtaLink from '../Global/CtaLink';
import SvgDownArrowSm from '../Global/ShowMoreArrow/SvgDownArrowSm';
import parse from 'html-react-parser';

interface Props {
    truncateBody?: boolean;
    gridSpan?: number;
    faq: FaqType;
    expanded?: boolean;
    isDisplayHidden?: boolean;
}

const safeTaxonomyNames = (
    taxonomy?: { value?: Array<{ name?: string }> | null } | null
): string[] => {
    if (!Array.isArray(taxonomy?.value)) return [];
    return taxonomy.value
        .map((item) => item?.name)
        .filter((name): name is string => Boolean(name));
};

export const isRenderableFaq = (faq: unknown): faq is FaqType =>
    Boolean((faq as FaqType | null | undefined)?.elements?.question?.value);

const Faq: React.FC<Props> = ({
    truncateBody,
    isDisplayHidden = false,
    ...props
}) => {
    const [expanded, setExpanded] = useState(props.expanded || false);
    const [truncated, setTruncated] = useState(false);
    const answerPanelId = useId();

    const faqStyle = props.gridSpan
        ? { gridColumn: `span ${props.gridSpan}` }
        : undefined;

    if (!isRenderableFaq(props.faq)) {
        return null;
    }

    const faqElements = props?.faq?.elements;

    const cta = {
        label: faqElements?.cta_configuration__cta_label?.value,
        location: BuildLinkHref(
            faqElements?.cta_configuration__cta_location,
            faqElements?.cta_configuration__cta_location_anchor_point
        ),
        target:
            getMultiChoice(
                faqElements?.cta_configuration__cta_window_action
            ) === 'new_window'
                ? '_blank'
                : 'self',
    };

    const renderTags = () => {
        if (!faqElements) return null;

        const blogTopics = safeTaxonomyNames(faqElements.blog_topic);
        const wellnessTypes = safeTaxonomyNames(faqElements.wellness_type);
        const country = safeTaxonomyNames(faqElements.country);
        const productFamily = safeTaxonomyNames(faqElements.product_family);
        const productType = safeTaxonomyNames(faqElements.product_type);
        const department = safeTaxonomyNames(faqElements.department);

        let tags = [];

        if (Array.isArray(productType)) {
            tags = [...tags, ...productType];
        }

        if (Array.isArray(blogTopics)) {
            tags = [...tags, ...blogTopics];
        }

        if (Array.isArray(wellnessTypes)) {
            tags = [...tags, ...wellnessTypes];
        }

        if (Array.isArray(country)) {
            tags = [...tags, ...country];
        }

        if (Array.isArray(department)) {
            tags = [...tags, ...department];
        }

        if (tags.length === 0) return null;

        return <div className={styles.faqTags}>{tags.join(', ')}</div>;
    };

    const toggleLines = (event: any) => {
        event.preventDefault();
        setExpanded(!expanded);
    };

    const parseHtml = (htmlAsString: string) => {
        let html = htmlAsString;
        html = html.replace(/&lt;/g, '<').replace(/&gt;/g, '>');
        html = html.replace(/\n/g, '');
        const faq = parse(html);
        return faq;
    };

    return (
        <div
            className={`${styles.faqExploration} ${
                isDisplayHidden ? styles.faqDisplayHidden : ''
            }`}
            style={faqStyle}
        >
            {renderTags()}
            <div
                className={styles.faqQuestion}
                onClick={toggleLines}
                data-kontent-element-codename="question"
            >
                {faqElements?.question?.value}
            </div>
            <div>
                <div id={answerPanelId} hidden={!expanded}>
                    {!truncated && (
                        <>
                            <div className={styles.faqAnswer}>
                                {RichText(
                                    faqElements?.answer,
                                    undefined,
                                    undefined,
                                    undefined,
                                    'answer'
                                )}
                            </div>
                            {cta.location && (
                                <div className={styles.faqCtaContainer}>
                                    <CtaLink
                                        href={cta.location}
                                        target={cta.target}
                                    >
                                        {cta.label}
                                    </CtaLink>
                                </div>
                            )}
                        </>
                    )}
                </div>
                <button
                    type="button"
                    className={`${styles.faqRowControls} ${
                        expanded ? styles.faqRowOpen : ''
                    } ${expanded ? styles.arrowUp : ''}`}
                    onClick={toggleLines}
                    aria-expanded={expanded}
                    aria-controls={answerPanelId}
                    aria-label={expanded ? 'Collapse answer' : 'Expand answer'}
                >
                    <SvgDownArrowSm strokeColor="#505251" transform={'0'} />
                </button>
            </div>
        </div>
    );
};

export default Faq;
